<template>
  <div
    class="flex flex-wrap items-center justify-between gap-4 border-b border-main px-6 py-4 last:border-b-0"
    :data-testid="'top-products-ranking-dashboard-card-' + tool.id"
  >
    <div class="flex flex-col gap-1">
      <p class="txt-label-m-semibold text-subtle">{{ tool.title }}</p>
      <div class="flex flex-wrap items-center gap-3">
        <p v-if="error" class="txt-body-s-regular text-subtle" :data-testid="'top-products-ranking-dashboard-error-' + tool.id">
          {{ t('ProductTop100Ranking.error') }}
        </p>
        <div
          v-else-if="!result"
          class="h-6 w-40 animate-pulse rounded-sm bg-surface-elevation-2"
          :data-testid="'top-products-ranking-dashboard-loading-' + tool.id"
          aria-hidden="true"
        ></div>
        <p v-else-if="rank" class="txt-headline-xl-bold tabular-nums" :data-testid="'top-products-ranking-dashboard-rank-' + tool.id">
          {{ t('ProductTop100Ranking.dashboard.rank', { rank }) }}
        </p>
        <p v-else class="min-h-6 txt-body-s-regular text-subtle" :data-testid="'top-products-ranking-dashboard-unranked-' + tool.id">
          {{ t('ProductTop100Ranking.dashboard.unranked') }}
        </p>
        <template v-if="pinned">
          <p class="flex items-center gap-3 txt-body-m-regular">
            <span class="h-6 border-l border-main" aria-hidden="true"></span>
            <StarIcon class="lucide-icon-sm text-icon-secondary" fill="currentColor" />
            {{ formatStars(pinned.rating) }}
          </p>
          <p class="flex items-center gap-3 txt-body-m-regular">
            <span class="h-6 border-l border-main" aria-hidden="true"></span>
            {{ t('ProductTop100Ranking.dashboard.reviews', pinned.reviewCount ?? 0) }}
          </p>
        </template>
      </div>
    </div>
    <RouterLink
      class="btn-primary-color-s"
      :data-testid="'top-products-ranking-dashboard-cta-' + tool.id"
      :to="{
        name: ReviewsRouteName.ReviewsProductTopProductsRanking,
        params: { productId: tool.id },
        query: { source: 'dashboard_widget' },
      }"
      @click="track('cta_click', { surface: 'dashboard', category: 'engagement', cta_id: 'top_products_ranking_dashboard' })"
    >
      {{ t('ProductTop100Ranking.dashboard.cta') }}
    </RouterLink>
  </div>
</template>

<script setup lang="ts">
import { StarIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useReviewsTopProductsRankingQuery } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types'

import type { TopProductsRankingDashboardTool } from './types'

const props = defineProps<{ tool: TopProductsRankingDashboardTool }>()

const { t, n } = useI18n()
const { result, error } = useReviewsTopProductsRankingQuery(
  { productSlug: props.tool.slug, page: 1, perPage: 1 },
  { clientId: 'reviews', fetchPolicy: 'cache-and-network', nextFetchPolicy: 'cache-first' },
)

const pinned = computed(() => result.value?.manage?.topProductsRanking?.pinned)
const rank = computed(() => pinned.value?.rank ?? undefined)
const formatStars = (value: number | null | undefined) =>
  value == null ? '–' : n(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
</script>
